import { AppHeaderBar } from "@/components/common/app-header-bar";
import { DataManagementEditor } from "@/components/ui/data-management/data-management-editor";
import { ViewerTool } from "@/store/features/features";
import { selectEnabledFeatures } from "@/store/features/features-slice";
import { useAppSelector } from "@/store/store-hooks";
import { useNavigateToDraftView } from "@/utils/redirects";
import { DataManagementBaseProviders } from "@faro-lotv/data-management";
import { useAuthContext } from "@faro-lotv/gate-keepers";
import { useApiClientContext } from "@faro-lotv/service-wires";
import { Stack } from "@mui/material";
import { useEffect, type JSX } from "react";

/**
 * @returns The page containing the Data Management editor provided by
 * the @faro-lotv/data-management library.
 * To be used by a <Route> in App.tsx, within the route-scoped project providers like the
 * `ProjectPage`.
 */
export function DataManagementPage(): JSX.Element {
  const viewerFeatures = useAppSelector(selectEnabledFeatures);
  const { projectApiClient } = useApiClientContext();
  const navigateToDraftView = useNavigateToDraftView();

  // Data Management requires a logged-in user.
  const { isLoggedIn, requestLogin } = useAuthContext();
  useEffect(() => {
    if (!isLoggedIn) {
      requestLogin();
    }
  }, [isLoggedIn, requestLogin]);

  if (isLoggedIn) {
    // Redirect to the Draft View route, which embeds Data Management.
    // Since the Draft View embeds Data Management and therefore also requires a logged-in user,
    // the login check above takes precedence over the redirection.
    navigateToDraftView(projectApiClient.projectId, "toggleButton");
  }
  return (
    <Stack sx={{ height: "100%" }}>
      <AppHeaderBar tool={ViewerTool.dataPreparationTool} />
      <DataManagementBaseProviders viewerFeatures={viewerFeatures}>
        <DataManagementEditor />
      </DataManagementBaseProviders>
    </Stack>
  );
}
